import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  ArrayField,
  ArrayInput,
  Button,
  Datagrid,
  FormDataConsumer,
  TextField,
  TextInput,
  SimpleFormIterator,
  useNotify,
  useInput,
  useRecordContext,
} from "react-admin";
import { useForm } from "react-final-form";
import {
  AcceptedIcon,
  CloseIcon,
  KeyboardOutlinedIcon,
  ScannerIcon,
} from "../../../components/common/iconography";
import { areAllSealsValid } from "../../../utils/utils";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    paddingTop: "5px",
    paddingBottom: "5px",
    justifyContent: "space-between",
  },
  title: {
    paddingTop: theme.spacing(1),
  },
  dialogContent: {
    minHeight: "50vh",
  },
  arraySeals: {
    width: "100%",
  },
  tableCellIcon: {
    padding: "3px",
    textAlign: "center",
    height: "33px",
  },
}));

const BarcodeInput = ({ source, dialogOpen = true, ...props }) => {
  const {
    input: { value, onChange },
    meta: { touched, error },
  } = useInput({ source, ...props });

  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(value)) {
      setInputValue(value.join(", "));
    } else {
      setInputValue("");
    }
  }, [value]);

  useEffect(() => {
    if (!dialogOpen) return undefined;

    const focusTimer = setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    return () => clearTimeout(focusTimer);
  }, [dialogOpen]);

  const handleChange = (event) => {
    const { value } = event.target;
    setInputValue(value);
    const arrayValue = value.split(/[, ]+/).map((item) => item.trim());
    onChange(arrayValue);
  };

  const parse = (value) => value.split(/[, ]+/).map((item) => item.trim());

  return (
    <TextInput
      {...props}
      source={source}
      value={inputValue}
      onChange={handleChange}
      variant="outlined"
      helperText={touched && error ? error : ""}
      parse={parse}
      hidden={true}
      inputRef={inputRef}
      fullWidth
    />
  );
};

const BooleanIconField = ({ source }) => {
  const record = useRecordContext();
  if (!record) return null;
  return record[source] ? (
    <ScannerIcon fontSize="small" />
  ) : (
    <KeyboardOutlinedIcon fontSize="small" />
  );
};

export const SealNumbersDialog = ({
  open,
  onClose,
  formData,
  SetAllSealsValid,
}) => {
  const [validSeals, setValidSeals] = useState({ items: [] });
  const [inValidSeals, setInValidSeals] = useState({ items: [] });
  const [inValidSealNumber, setInValidSealNumber] = useState([]);
  const classes = useStyles();
  const form = useForm();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const notify = useNotify();

  const getInvalidSealnumbers = (sealNumbers, sealsObjectItems) => {
    const sealsFromObject = sealsObjectItems
      .map((item) => item.validated && item.barcode)
      .filter(Boolean);
    return sealNumbers?.filter((el) => !sealsFromObject.includes(el));
  };

  useEffect(() => {
    SetAllSealsValid(
      areAllSealsValid(
        formData?.inValidSealNumber,
        formData?.inValidSeals?.items
      )
    );
  }, [formData?.inValidSealNumber, formData?.inValidSeals?.items, SetAllSealsValid]);

  useEffect(() => {
    if (formData?.sealNumbers && formData?.seals?.items) {
      form.change(
        "inValidSealNumber",
        getInvalidSealnumbers(formData.sealNumbers, formData.seals.items)
      );
    }
    if (formData?.seals?.items) {
      form.change("inValidSeals", {
        items: formData.seals.items.filter((el) => !el.validated),
      });
      const updatedSeals = formData.seals.items.filter((el) => el.validated);
      setValidSeals({ items: updatedSeals });
      setInValidSeals({
        items: formData.seals.items.filter((el) => !el.validated),
      });
      setInValidSealNumber(
        getInvalidSealnumbers(formData.sealNumbers, formData.seals.items)
      );
    }
  }, [form, formData?.sealNumbers, formData?.seals?.items]);

  function arraysEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) {
      return false;
    }
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
        return false;
      }
    }
    return true;
  }
  function objectEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) {
      return false;
    }
    for (let i = 0; i < arr1.length; i++) {
      if (
        arr1[i].barcode !== arr2[i].barcode ||
        arr1[i].validated !== arr2[i].validated
      ) {
        return false;
      }
    }
    return true;
  }

  const handleClose = () => {
    if (
      !arraysEqual(formData.inValidSealNumber, inValidSealNumber) ||
      !objectEqual(formData.inValidSeals.items, inValidSeals.items)
    ) {
      form.change("sealNumbers", [
        ...formData.inValidSealNumber,
        ...validSeals.items.map((item) => item.barcode),
      ]);
    }
    if (
      arraysEqual(formData.inValidSealNumber, inValidSealNumber) &&
      objectEqual(formData.inValidSeals.items, inValidSeals.items)
    ) {
      form.change("sealNumbers", form.getFieldState("sealNumbers").initial);
    }
    onClose();
  };
  const handlevalidation = (scopedFormData, getSource) => {
    if (formData.inValidSealNumber.includes(scopedFormData.barcode)) {
      form.change(getSource("validated"), true);
      notify("Seal successfully validated.", "info");
    } else {
      notify("Unable to match seal numbers.", "error");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={isSmall ? "xxl" : "lg"}
      fullWidth
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography className={classes.title} variant="h6">
          Seal Manager
        </Typography>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <Grid container spacing={2}>
          {formData?.inValidSeals?.items?.length > 0 ||
          formData?.inValidSealNumber?.length > 0 ? (
            <>
              <Grid item xs={12}>
                <Typography variant="overline" color="textSecondary">
                  Unmatched Seals
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <ArrayInput
                      source="inValidSealNumber"
                      label="Advised seals"
                    >
                      <SimpleFormIterator>
                        <FormDataConsumer>
                          {({ getSource, formData, scopedFormData }) => {
                            const sealsFrominvalidObject =
                              formData.inValidSeals.items
                                .map((item) => item.validated && item.barcode)
                                .filter(Boolean);
                            return (
                              <TextInput
                                variant="outlined"
                                label=""
                                source={getSource("")}
                                fullWidth
                                disabled={sealsFrominvalidObject.includes(
                                  scopedFormData
                                )}
                              />
                            );
                          }}
                        </FormDataConsumer>
                      </SimpleFormIterator>
                    </ArrayInput>
                  </Grid>
                  <Grid item xs={0.5}>
                    <Divider orientation="vertical" variant="middle" />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <ArrayInput
                      source="inValidSeals.items"
                      label="Entered seals"
                      fullWidth
                    >
                      <SimpleFormIterator
                        disableReordering
                        disableAdd
                        disableRemove
                      >
                        <FormDataConsumer>
                          {({ getSource, formData, scopedFormData }) => {
                            const sealsFrominvalidObject =
                              formData.inValidSeals.items
                                .map((item) => item.validated && item.barcode)
                                .filter(Boolean);
                            return (
                              <Box display="flex">
                                <TextInput
                                  variant="outlined"
                                  label=""
                                  source={getSource("barcode")}
                                  fullWidth
                                  disabled={sealsFrominvalidObject.includes(
                                    scopedFormData.barcode
                                  )}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        {scopedFormData?.isScanned ? (
                                          <ScannerIcon />
                                        ) : (
                                          <KeyboardOutlinedIcon />
                                        )}
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                                <Box mt={1}>
                                  <Button
                                    onClick={() =>
                                      handlevalidation(
                                        scopedFormData,
                                        getSource
                                      )
                                    }
                                    label="Validate"
                                    color="default"
                                  >
                                    <AcceptedIcon />
                                  </Button>
                                </Box>
                              </Box>
                            );
                          }}
                        </FormDataConsumer>
                      </SimpleFormIterator>
                    </ArrayInput>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Divider variant="middle" />
              </Grid>
            </>
          ) : (
            ""
          )}
          <Grid item xs={12}>
            <Typography variant="overline" color="textSecondary">
              Validated Seals
            </Typography>
            <ArrayField
              source="items"
              label="Valid seals"
              fullWidth
              record={validSeals}
            >
              <Grid container direction="row">
                <Grid item xs={12} md={1}>
                  <Datagrid>
                    <BooleanIconField
                      source="isScanned"
                      label="&nbsp;"
                      fullWidth
                      emptyText="&nbsp;"
                      cellClassName={classes.tableCellIcon}
                    />
                  </Datagrid>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Datagrid>
                    <TextField source="barcode" fullWidth emptyText="&nbsp;" />
                  </Datagrid>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Datagrid>
                    <TextField
                      source="createdBy"
                      label="Entered By"
                      fullWidth
                      emptyText="&nbsp;"
                    />
                  </Datagrid>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Datagrid>
                    <TextField
                      source="description"
                      fullWidth
                      emptyText="&nbsp;"
                    />
                  </Datagrid>
                </Grid>
              </Grid>
            </ArrayField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} label="Close" color="default" />
      </DialogActions>
    </Dialog>
  );
};

export const SealNumbersCreateDialog = ({ open, onClose, formData }) => {
  const classes = useStyles();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={isSmall ? "xxl" : "lg"}
      fullWidth
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography className={classes.title} variant="h6">
          Seal Manager
        </Typography>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <BarcodeInput source="sealNumbers" dialogOpen={open} />
          </Grid>
          <Grid item xs={12}>
            <ArrayInput
              source="sealNumbers"
              label="Advised Seals"
              className={classes.arraySeals}
            >
              <SimpleFormIterator disableReordering>
                <FormDataConsumer>
                  {({ getSource }) => (
                    <Grid>
                      <TextInput
                        source={getSource("")}
                        label=""
                        variant="outlined"
                        helperText={false}
                      />
                    </Grid>
                  )}
                </FormDataConsumer>
              </SimpleFormIterator>
            </ArrayInput>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} label="Close" color="default" />
      </DialogActions>
    </Dialog>
  );
};
